import React from 'react';
import {
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	RadioGroup,
	Radio
} from '@heroui/react';

/**
 * MoveConfirmModal — confirmation dialog for moves on recurring events.
 *
 * Trigger flow (Rem16 Sub 6 → Rem17 Sub 2 wiring): `MonthGrid.handleDropEvent`
 * detects a recurring + non-maintenance drag and opens this modal BEFORE
 * dispatching `moveEventThunk`. Once the user picks a scope, `MonthGrid`
 * dispatches the thunk with `eventsForAction` set on the FIRST request.
 * The post-failure `moveErrorRequiresConfirmation` path is retained as a
 * defensive fallback for any backend-flagged confirmation scenarios beyond
 * the recurring case.
 *
 * The modal is intentionally dumb — it owns no state beyond the radio
 * selection. Parent passes `pendingMove` so the modal can show context.
 */
// Values MUST match the Symfony `EventParamsEventsForActionType` enum
// for MOVE actions. `CalendarEventValidationManager.php:594-596` explicitly
// throws on `'all'` for MOVE (backend regression test
// `testHandleMoveActionAllThrowsLegacyEquivalentExceptionBeforeQueryingEvents`).
// Valid MOVE scopes per `EventParamsEventsForActionType.php:14-20`:
//   current      — only this occurrence
//   not-started  — this + all not-yet-started occurrences
//   following    — this + all subsequent occurrences (started or not)
//
// Previous `single` (Phase 2) and `all` (Rem16 Sub 6) were both wrong —
// `single` didn't exist on the enum at all, and `all` is illegal for MOVE.
const OPTIONS = [
	{ value: 'current', label: 'Only this event' },
	{ value: 'not-started', label: 'This and not-started events' },
	{ value: 'following', label: 'This and the following events' }
];

function MoveConfirmModal({ isOpen, onClose, onConfirm, pendingMove }) {
	const [choice, setChoice] = React.useState('current');

	React.useEffect(() => {
		if (isOpen) setChoice('current');
	}, [isOpen]);

	const eventTitle =
		pendingMove?.event?.title ||
		pendingMove?.event?.aircraftName ||
		'event';

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="sm" classNames={{ base: 'shadow-cal-elev-3' }}>
			<ModalContent>
				{close => (
					<>
						<ModalHeader>Move recurring event</ModalHeader>
						<ModalBody>
							<p className="text-sm text-default-600 mb-2">
								&quot;{eventTitle}&quot; is part of a recurring series. How should the
								move apply?
							</p>
							<RadioGroup value={choice} onValueChange={setChoice}>
								{OPTIONS.map(opt => (
									<Radio key={opt.value} value={opt.value}>
										{opt.label}
									</Radio>
								))}
							</RadioGroup>
						</ModalBody>
						<ModalFooter>
							<Button variant="light" onPress={close}>
								Cancel
							</Button>
							<Button
								color="primary"
								onPress={() => {
									if (typeof onConfirm === 'function') onConfirm(choice);
								}}
							>
								Confirm
							</Button>
						</ModalFooter>
					</>
				)}
			</ModalContent>
		</Modal>
	);
}

export default MoveConfirmModal;
